import { useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { Label } from '@/shared/ui/label'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { AddLoggerInline, type CreatedLogger } from '@/features/task-logging/AddLoggerInline'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/shared/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { t } from '@/shared/lib/i18n'
import { RICH_TEXT_DISPLAY_CLASSNAME, sanitizeRichTextHtml } from '@/shared/lib/rich-text-html'
import { cn } from '@/shared/lib/utils'
import { commentsPath } from '@/shared/lib/routes'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { SharedProps } from '@/shared/types/SharedProps'
import type { WebComment } from '@/shared/types/web'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface CommentsPanelProps {
  comments: WebComment[]
  commentableType: string
  commentableId: number
  onCommentChange?: () => void
  compact?: boolean
  hideTitle?: boolean
  hideForm?: boolean
  disableEdit?: boolean
  /**
   * REBUILD-531: open-logging context for report-comment authorship. When 'OL' the add-comment
   * flow offers logger selection + "Add new user"; otherwise (PR / absent) the comment is
   * authored by the current user and these controls are hidden.
   */
  logAuthenticationCode?: string | null
  /** Logger picklist for the open-logging add-comment flow (no prepended current-user row). */
  loggerOptions?: { id: number; label: string }[]
  /** Instrument whose effective open-logging code gates the inline "Add new user" create. */
  instrumentId?: number | null
}

interface CreateFormData {
  body: string
  commentableType: string
  commentableId: number
  labLoggerId: string
}

interface EditFormData {
  body: string
}

const isHtmlContent = (text: string): boolean => /<[a-z][\s\S]*>/i.test(text)

export const CommentBody = ({ body, className }: { body: string; className?: string }) => {
  if (!body) return null

  if (isHtmlContent(body)) {
    return (
      <div
        className={cn(RICH_TEXT_DISPLAY_CLASSNAME, className)}
        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(body) }}
      />
    )
  }

  return <p className={cn('text-sm whitespace-pre-wrap', className)}>{body}</p>
}

const CommentItem = ({
  comment,
  currentUserId,
  canDelete,
  disableEdit,
  onCommentChange,
}: {
  comment: WebComment
  currentUserId: number | null
  canDelete: boolean
  disableEdit: boolean
  onCommentChange?: () => void
}) => {
  const { formatDateTime } = useSiteDateTime()
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const editForm = useForm<EditFormData>({ body: comment.body })

  const isOwn = currentUserId !== null && comment.userId === currentUserId
  const canDeleteThis = isOwn || canDelete

  const handleSave = () => {
    editForm.patch(comment.commentPath, {
      preserveScroll: true,
      onSuccess: () => {
        setEditing(false)
        onCommentChange?.()
      },
    })
  }

  const handleDelete = () => {
    setDeleting(true)
    router.delete(comment.commentPath, {
      preserveScroll: true,
      onFinish: () => setDeleting(false),
      onSuccess: () => {
        setConfirmingDelete(false)
        onCommentChange?.()
      },
    })
  }

  return (
    <div className="group/comment space-y-1 border-b py-3 last:border-b-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-medium">{comment.authorName}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {formatDateTime(comment.createdAt, comment.timeZoneName)}
          </span>
        </div>

        {!editing && disableEdit && canDeleteThis && (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t('instruments.taskLogs.commentsPanel.delete')}
            onClick={() => setConfirmingDelete(true)}
            className="text-muted-foreground -mr-1 hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </Button>
        )}

        {!editing && !disableEdit && canDeleteThis && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={t('instruments.taskLogs.commentsPanel.moreActions')}
                className="text-muted-foreground -mr-1 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/comment:opacity-100 data-[state=open]:opacity-100 max-sm:opacity-100"
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isOwn && (
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  <Pencil className="mr-2 size-4" />
                  {t('instruments.taskLogs.commentsPanel.edit')}
                </DropdownMenuItem>
              )}
              {canDeleteThis && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    setConfirmingDelete(true)
                  }}
                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <Trash2 className="mr-2 size-4" />
                  {t('instruments.taskLogs.commentsPanel.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {editing ? (
        <div className="space-y-2 pt-1">
          <RichTextEditor
            value={editForm.data.body}
            onChange={(htmlValue) => editForm.setData('body', htmlValue)}
          />
          {editForm.errors.body && (
            <p className="text-destructive text-sm" role="alert">
              {editForm.errors.body}
            </p>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={editForm.processing}>
              {editForm.processing
                ? t('instruments.taskLogs.commentsPanel.saving')
                : t('instruments.taskLogs.commentsPanel.save')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t('instruments.taskLogs.commentsPanel.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <CommentBody body={comment.body} />
      )}

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('instruments.taskLogs.commentsPanel.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('instruments.taskLogs.commentsPanel.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('instruments.taskLogs.commentsPanel.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={handleDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting
                ? t('instruments.taskLogs.commentsPanel.deleting')
                : t('instruments.taskLogs.commentsPanel.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export const CommentsPanel = ({
  comments,
  commentableType,
  commentableId,
  onCommentChange,
  compact = false,
  hideTitle = false,
  hideForm = false,
  disableEdit = false,
  logAuthenticationCode,
  loggerOptions,
  instrumentId,
}: CommentsPanelProps) => {
  const { props: shared } = usePage<SharedProps>()
  const currentUserId = shared.currentUserInfo?.id ?? null
  const canCreate = useCan(Resources.COMMENTS, Actions.CREATE)
  const canDelete = useCan(Resources.COMMENTS, Actions.DELETE)

  // REBUILD-531: open-logging comment authorship. Under OL the author is a selected logger (not
  // the current user), so the picker + "Add new user" are shown and a logger must be chosen.
  const isOpenLogging = logAuthenticationCode === 'OL'
  const [addedLoggers, setAddedLoggers] = useState<{ id: number; label: string }[]>([])
  const mergedLoggerOptions = [
    ...(loggerOptions ?? []),
    ...addedLoggers.filter(
      (added) => !(loggerOptions ?? []).some((option) => option.id === added.id),
    ),
  ]

  const createForm = useForm<CreateFormData>({
    body: '',
    commentableType,
    commentableId,
    labLoggerId: '',
  })

  const handleLoggerCreated = (logger: CreatedLogger) => {
    setAddedLoggers((previous) =>
      previous.some((existing) => existing.id === logger.id)
        ? previous
        : [...previous, { id: logger.id, label: logger.label }],
    )
    createForm.setData('labLoggerId', String(logger.id))
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createForm.transform((data) => ({
      body: data.body,
      commentable_type: data.commentableType,
      commentable_id: data.commentableId,
      ...(isOpenLogging && data.labLoggerId ? { lab_logger_id: data.labLoggerId } : {}),
    }))
    createForm.post(commentsPath(), {
      preserveScroll: true,
      onSuccess: () => {
        createForm.setData('body', '')
        createForm.setData('labLoggerId', '')
        onCommentChange?.()
      },
    })
  }

  const baseError = (createForm.errors as Record<string, string | string[] | undefined>).base
  const baseErrorMessage = Array.isArray(baseError) ? baseError[0] : baseError

  return (
    <section className={compact ? 'space-y-3' : 'space-y-4 rounded-lg border p-4'}>
      {!hideTitle && (
        <h2 className={compact ? 'text-sm font-semibold' : 'text-lg font-semibold'}>
          {t('instruments.taskLogs.commentsPanel.sectionTitle')}
        </h2>
      )}

      {comments.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t('instruments.taskLogs.commentsPanel.empty')}
        </p>
      ) : (
        <div>
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              canDelete={canDelete}
              disableEdit={disableEdit}
              onCommentChange={onCommentChange}
            />
          ))}
        </div>
      )}

      {canCreate && !hideForm && (
        <form onSubmit={handleCreate} className="space-y-3 border-t pt-4">
          {isOpenLogging && (
            <div className="space-y-2">
              <div className="space-y-1.5">
                <Label htmlFor="comment-logger">{t('instruments.taskLogs.logger')}</Label>
                <SearchableSelect
                  id="comment-logger"
                  value={createForm.data.labLoggerId || ''}
                  onValueChange={(selectedValue) =>
                    createForm.setData('labLoggerId', selectedValue)
                  }
                  placeholder={t('instruments.taskLogs.selectLogger')}
                  options={mergedLoggerOptions.map((loggerOption) => ({
                    value: String(loggerOption.id),
                    label: loggerOption.label,
                  }))}
                />
              </div>
              <AddLoggerInline instrumentId={instrumentId} onCreated={handleLoggerCreated} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="comment-body">{t('instruments.taskLogs.commentsPanel.addLabel')}</Label>
            <RichTextEditor
              id="comment-body"
              value={createForm.data.body}
              onChange={(htmlValue) => createForm.setData('body', htmlValue)}
              placeholder={t('instruments.taskLogs.commentsPanel.placeholder')}
              className={baseErrorMessage ? 'border-destructive' : ''}
            />
            {baseErrorMessage && (
              <p className="text-destructive text-sm" role="alert">
                {baseErrorMessage}
              </p>
            )}
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={
              createForm.processing ||
              !createForm.data.body.trim() ||
              (isOpenLogging && !createForm.data.labLoggerId)
            }
          >
            {createForm.processing
              ? t('instruments.taskLogs.commentsPanel.posting')
              : t('instruments.taskLogs.commentsPanel.post')}
          </Button>
        </form>
      )}
    </section>
  )
}
